import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { getVisitorModuleCapabilities } from '../../config/visitorCapabilities';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useVisitors } from '../../hooks/useVisitors';
import { deleteVisitor } from '../../services/visitorService';
import type { Visitor } from '../../types/visitor';
import { confirmAction } from '../../utils/confirmAction';
import { formatDateTime } from '../../utils/dateTime';
import { showOperationFeedback } from '../../utils/operationFeedback';
import { visitorErrorMessage } from '../../utils/visitorErrors';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'VisitorList'>;

export function VisitorListScreen({ navigation }: Props) {
  const palette = useAppColors();
  const auth = useAuth();
  const capabilities = getVisitorModuleCapabilities(auth.permissions);
  const list = useVisitors();
  const [removingId, setRemovingId] = useState<number | null>(null);

  const themed = useMemo(
    () => ({
      addButton: {
        backgroundColor: palette.primary,
      },
      addText: { color: palette.onPrimary },
      name: { ...typography.body, color: palette.textPrimary, fontWeight: '600' as const },
      meta: { ...typography.caption, color: palette.textSecondary },
      muted: { ...typography.caption, color: palette.textMuted },
      remove: { ...typography.caption, color: palette.error, fontWeight: '600' as const },
    }),
    [palette],
  );

  const removeVisitor = useCallback(
    (visitor: Visitor) => {
      confirmAction({
        title: 'Remove visitor',
        message: `Remove ${visitor.visitorName}? Their face data is erased; visit history is kept.`,
        confirmText: 'Remove',
        destructive: true,
        onConfirm: async () => {
          setRemovingId(visitor.visitorId);
          try {
            await deleteVisitor(visitor.visitorId);
            showOperationFeedback('delete', 'success');
            list.refresh();
          } catch (error) {
            showOperationFeedback(
              'delete',
              'failure',
              visitorErrorMessage(error, 'Unable to remove the visitor.'),
            );
          } finally {
            setRemovingId(null);
          }
        },
      });
    },
    [list],
  );

  const addButton = capabilities.canManageVisitors ? (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel="Register visitor"
      onPress={() => navigation.navigate(routes.visitorRegister)}
      style={[styles.addButton, themed.addButton]}
    >
      <Text style={[styles.addText, themed.addText]}>+</Text>
    </Pressable>
  ) : null;

  const footer = list.isLoadingMore ? (
    <View style={styles.footer}>
      <ActivityIndicator color={palette.primary} />
    </View>
  ) : null;

  const hasSearch = list.search.trim().length > 0;

  return (
    <PermissionGuard
      permission={PERMISSIONS.VISITOR_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen contentStyle={styles.screen}>
        <ScreenHeader title="Visitors" onBack={navigation.goBack} right={addButton} />
        <View style={styles.body}>
          <View style={styles.actions}>
            {capabilities.canManageVisitors ? (
              <>
                <AppButton
                  title="Visitor check-in"
                  onPress={() => navigation.navigate(routes.visitorCheckIn)}
                  style={styles.action}
                />
                <AppButton
                  title="Visitor check-out"
                  variant="secondary"
                  onPress={() => navigation.navigate(routes.visitorCheckOut)}
                  style={styles.action}
                />
              </>
            ) : null}
            <AppButton
              title="Visitor history"
              variant="secondary"
              onPress={() => navigation.navigate(routes.visitorHistory)}
              style={styles.action}
            />
          </View>

          <AppInput
            label="Search"
            value={list.search}
            onChangeText={list.setSearch}
            placeholder="Name, company or phone"
            autoCapitalize="none"
          />

          {list.isLoading && list.items.length === 0 ? (
            <View style={styles.center}>
              <ActivityIndicator color={palette.primary} accessibilityLabel="Loading visitors" />
            </View>
          ) : list.error && list.items.length === 0 ? (
            <ErrorState message={list.error} onRetry={list.retry} />
          ) : (
            <FlatList
              data={list.items}
              keyExtractor={item => String(item.visitorId)}
              refreshControl={
                <RefreshControl refreshing={list.isRefreshing} onRefresh={list.refresh} />
              }
              onEndReached={list.loadMore}
              onEndReachedThreshold={0.4}
              ListFooterComponent={footer}
              contentContainerStyle={styles.listContent}
              ListEmptyComponent={
                <EmptyState
                  title={hasSearch ? 'No visitors match your search.' : 'No visitors registered yet.'}
                  message={
                    hasSearch
                      ? 'Try a different name, company or phone number.'
                      : capabilities.canManageVisitors
                        ? 'Tap + to register your first visitor.'
                        : 'Registered visitors will appear here.'
                  }
                  actionTitle={hasSearch ? 'Clear search' : undefined}
                  onAction={hasSearch ? () => list.setSearch('') : undefined}
                />
              }
              renderItem={({ item }) => (
                <AppCard style={styles.card}>
                  <View style={styles.cardRow}>
                    <View style={styles.cardText}>
                      <Text style={themed.name}>{item.visitorName}</Text>
                      <Text style={themed.meta}>
                        {[item.designation, item.companyName].filter(Boolean).join(' · ')}
                      </Text>
                      <Text style={themed.meta}>{item.phoneNumber}</Text>
                      {item.purposeOfVisit ? (
                        <Text style={themed.muted}>Purpose: {item.purposeOfVisit}</Text>
                      ) : null}
                      {item.lastCheckInAt ? (
                        <Text style={themed.muted}>
                          Last visit: {formatDateTime(item.lastCheckInAt)}
                        </Text>
                      ) : null}
                    </View>
                    <StatusBadge label={item.isInside ? 'Inside' : 'Not inside'} />
                  </View>
                  {capabilities.canManageVisitors && !item.isInside ? (
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={`Remove ${item.visitorName}`}
                      disabled={removingId === item.visitorId}
                      onPress={() => removeVisitor(item)}
                      style={styles.removeButton}
                    >
                      <Text style={themed.remove}>
                        {removingId === item.visitorId ? 'Removing…' : 'Remove'}
                      </Text>
                    </Pressable>
                  ) : null}
                </AppCard>
              )}
            />
          )}
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0 },
  body: { flex: 1, marginTop: spacing.lg, gap: spacing.sm },
  actions: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  action: { flexGrow: 1, minWidth: 140 },
  addButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addText: { fontSize: 26, lineHeight: 28, fontWeight: '600' },
  listContent: { flexGrow: 1, paddingBottom: spacing.xl, gap: spacing.sm },
  card: { gap: spacing.sm },
  cardRow: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm },
  cardText: { flex: 1, gap: spacing.xs },
  removeButton: { alignSelf: 'flex-start', paddingVertical: spacing.xs },
  footer: { paddingVertical: spacing.lg, alignItems: 'center' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
});
